import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FileText, CalendarCheck, Layers, Dumbbell, FileQuestion, ImageIcon, StickyNote, MoreHorizontal, Pencil, Trash2, Download, MessageSquare, Search, Loader2, Shapes } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { downloadFrom, saveBlob, safeName } from "@/lib/download";
import { EmptyState, SkeletonList } from "@/components/Mascot";
import { Markdown } from "@/components/ChatMessage";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const KINDS = {
  document: { icon: FileText, cls: "text-sky-300 bg-sky-400/10", label: "Documents" },
  study_plan: { icon: CalendarCheck, cls: "text-yellow-200 bg-yellow-300/10", label: "Study plans" },
  flashcards: { icon: Layers, cls: "text-emerald-300 bg-emerald-400/10", label: "Flashcard decks" },
  practice: { icon: Dumbbell, cls: "text-sky-300 bg-sky-400/10", label: "Practice sets" },
  exam: { icon: FileQuestion, cls: "text-violet-300 bg-violet-400/10", label: "Mock exams" },
  image: { icon: ImageIcon, cls: "text-violet-300 bg-violet-400/10", label: "Draw it images" },
  note: { icon: StickyNote, cls: "text-neutral-300 bg-white/[0.06]", label: "Saved answers" },
};
const SORTS = { newest: (a, b) => b.created_at.localeCompare(a.created_at), oldest: (a, b) => a.created_at.localeCompare(b.created_at), name: (a, b) => a.title.localeCompare(b.title) };

export function ArtifactIcon({ kind }) {
  const k = KINDS[kind] || KINDS.note;
  return <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${k.cls}`} aria-hidden="true"><k.icon className="h-4 w-4 stroke-[1.7]" /></span>;
}

const ROUTE = { practice: (a) => `/app/practice?set=${a.ref}`, exam: (a) => `/app/practice?set=${a.ref}`, flashcards: () => "/app/flashcards", study_plan: (a) => `/app/exams/${a.ref}` };

async function download(a) {
  try {
    if (a.kind === "document") await downloadFrom(`/documents/${a.ref}/download`, `${safeName(a.title)}.${a.ext || "docx"}`);
    else if (a.kind === "image") await downloadFrom(`/diagrams/${a.ref}/image`, `${safeName(a.title)}.png`);
    else if (a.kind === "note") saveBlob(new Blob([`# ${a.title}\n\n${a.content || ""}`], { type: "text/markdown" }), `${safeName(a.title)}.md`);
  } catch (e) { toast.error(formatError(e)); }
}

function ImagePreview({ id, alt }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    http.get(`/diagrams/${id}/image`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => setSrc(""));
    return () => url && URL.revokeObjectURL(url);
  }, [id]);
  if (src === null) return <Loader2 className="h-5 w-5 animate-spin mx-auto my-10 text-neutral-500" />;
  return src ? <img src={src} alt={alt} className="w-full rounded-xl bg-white" data-testid="artifact-preview-image" /> : <p className="text-sm text-neutral-500">This image couldn't be loaded.</p>;
}

function Preview({ a, onClose }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121725] border-white/10 max-w-3xl sm:rounded-2xl" data-testid="artifact-preview">
        <DialogHeader>
          <DialogTitle className="font-display pr-6">{a.title}</DialogTitle>
          <DialogDescription className="text-neutral-500">{a.type} · {new Date(a.created_at).toLocaleDateString()}</DialogDescription>
        </DialogHeader>
        {a.kind === "image" && <ImagePreview id={a.ref} alt={a.title} />}
        {a.kind === "note" && <div className="max-h-[55vh] overflow-y-auto scroll-thin"><Markdown>{a.content || ""}</Markdown></div>}
        {a.kind === "document" && <p className="text-sm text-neutral-400">Download the file to open it in your usual app.</p>}
        <div className="flex flex-wrap gap-2">
          {a.downloadable && <button onClick={() => download(a)} data-testid="artifact-preview-download" className="h-10 px-4 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500 inline-flex items-center gap-2"><Download className="h-4 w-4" />Download</button>}
          {a.chat_id && <Link to={`/app/c/${a.chat_id}`} data-testid="artifact-preview-chat" className="h-10 px-4 rounded-full border border-white/15 text-sm hover:bg-white/5 inline-flex items-center gap-2"><MessageSquare className="h-4 w-4" />Open the chat it came from</Link>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RenameDialog({ a, onClose, onSaved }) {
  const [v, setV] = useState(a.title);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!v.trim()) return;
    setBusy(true);
    try { const { data } = await http.patch(`/artifacts/${encodeURIComponent(a.id)}`, { title: v }); onSaved(data.title); toast.success("Renamed"); onClose(); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121725] border-white/10 max-w-md sm:rounded-2xl" data-testid="artifact-rename-dialog">
        <DialogHeader><DialogTitle className="font-display">Rename</DialogTitle><DialogDescription className="text-neutral-500">{a.type}</DialogDescription></DialogHeader>
        <form onSubmit={save} className="flex gap-2">
          <Input autoFocus value={v} onChange={(e) => setV(e.target.value)} maxLength={120} className="bg-white/5 border-white/10" aria-label="New name" data-testid="artifact-rename-input" />
          <button disabled={busy || !v.trim()} className="px-4 rounded-lg bg-sky-400 text-white text-sm font-medium hover:bg-sky-500 disabled:opacity-50" data-testid="artifact-rename-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ a, onClose, onDeleted }) {
  const del = async () => {
    try { await http.delete(`/artifacts/${encodeURIComponent(a.id)}`); onDeleted(); toast.success("Deleted"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const what = a.kind === "flashcards" ? `all ${a.count || ""} cards in this deck` : a.kind === "study_plan" ? "this study plan (the exam itself stays)" : "it permanently";
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="bg-[#121725] border-white/10 sm:rounded-2xl" data-testid="artifact-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">Delete "{a.title}"?</AlertDialogTitle>
          <AlertDialogDescription className="text-neutral-400">This removes {what}. It can't be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-transparent border-white/15 hover:bg-white/5" data-testid="artifact-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={del} className="bg-red-500 hover:bg-red-600 text-white" data-testid="artifact-delete-confirm">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Row({ a, onOpen, onRename, onDelete }) {
  return (
    <li className="group flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.04] transition-colors" data-testid={`artifact-${a.id}`}>
      <ArtifactIcon kind={a.kind} />
      <button onClick={() => onOpen(a)} className="flex-1 min-w-0 text-left outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70 rounded-md" data-testid={`artifact-open-${a.id}`}>
        <span className="block text-sm text-neutral-100 truncate">{a.title}</span>
        <span className="block text-xs text-neutral-500 truncate">{a.type}{a.count ? ` · ${a.count} cards` : ""}{a.subject ? ` · ${a.subject}` : ""}</span>
      </button>
      <span className="hidden sm:block text-xs text-neutral-500 w-24 text-right shrink-0">{new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button aria-label={`Options for ${a.title}`} data-testid={`artifact-menu-${a.id}`} className="h-8 w-8 rounded-lg flex items-center justify-center text-neutral-400 hover:text-white hover:bg-white/10 outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70"><MoreHorizontal className="h-4 w-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#161B28] border-white/10 min-w-[190px]">
          <DropdownMenuItem onClick={() => onOpen(a)} data-testid={`artifact-menu-open-${a.id}`}>Open</DropdownMenuItem>
          {a.downloadable && <DropdownMenuItem onClick={() => download(a)} data-testid={`artifact-menu-download-${a.id}`}><Download className="h-3.5 w-3.5 mr-2" />Download</DropdownMenuItem>}
          {a.chat_id && <DropdownMenuItem asChild data-testid={`artifact-menu-chat-${a.id}`}><Link to={`/app/c/${a.chat_id}`}><MessageSquare className="h-3.5 w-3.5 mr-2" />Open its chat</Link></DropdownMenuItem>}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onRename(a)} data-testid={`artifact-menu-rename-${a.id}`}><Pencil className="h-3.5 w-3.5 mr-2" />Rename</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDelete(a)} className="text-red-300 focus:text-red-200" data-testid={`artifact-menu-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5 mr-2" />Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

function Toolbar({ q, setQ, type, setType, sort, setSort, kinds }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <div className="relative flex-1">
        <Search className="h-4 w-4 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search artifacts…" aria-label="Search artifacts" className="pl-9 h-10 bg-white/5 border-white/10" data-testid="artifacts-search" />
      </div>
      <Select value={type} onValueChange={setType}>
        <SelectTrigger className="h-10 sm:w-48 bg-white/5 border-white/10" aria-label="Filter by type" data-testid="artifacts-type-filter"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#161B28] border-white/10">
          <SelectItem value="all">All types</SelectItem>
          {kinds.map((k) => <SelectItem key={k} value={k} data-testid={`artifacts-type-${k}`}>{KINDS[k].label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={sort} onValueChange={setSort}>
        <SelectTrigger className="h-10 sm:w-36 bg-white/5 border-white/10" aria-label="Sort" data-testid="artifacts-sort"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#161B28] border-white/10">
          <SelectItem value="newest">Newest</SelectItem><SelectItem value="oldest">Oldest</SelectItem><SelectItem value="name">Name</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

export default function ArtifactsPage() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [sort, setSort] = useState("newest");
  const [dialog, setDialog] = useState(null);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const load = useCallback(() => http.get("/artifacts").then((r) => setItems(r.data)).catch((e) => { toast.error(formatError(e)); setItems([]); }), []);
  useEffect(() => { load(); }, [load]);
  const open = useCallback((a) => (ROUTE[a.kind] ? navigate(ROUTE[a.kind](a)) : setDialog({ mode: "preview", a })), [navigate]);
  useEffect(() => {
    const id = params.get("open");
    const a = id && items?.find((x) => x.id === id);
    if (a) { setParams({}, { replace: true }); open(a); }
  }, [items, params, setParams, open]);
  const kinds = useMemo(() => Object.keys(KINDS).filter((k) => (items || []).some((a) => a.kind === k)), [items]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (items || []).filter((a) => (type === "all" || a.kind === type) && (!t || `${a.title} ${a.type} ${a.subject || ""}`.toLowerCase().includes(t))).sort(SORTS[sort]);
  }, [items, q, type, sort]);
  const patch = (id, ch) => setItems((x) => x.map((a) => (a.id === id ? { ...a, ...ch } : a)));
  const close = () => setDialog(null);
  return (
    <div className="flex-1 overflow-y-auto scroll-thin">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6" data-testid="artifacts-page">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight flex items-center gap-3"><Shapes className="h-8 w-8 text-sky-300" />Artifacts</h1>
          <p className="text-neutral-400 mt-3 max-w-xl">Everything Bryn has made for you: documents, study plans, flashcards, practice sets, mock exams and images. They save automatically.</p>
        </div>
        {items?.length > 0 && <Toolbar {...{ q, setQ, type, setType, sort, setSort, kinds }} />}
        {!items && <SkeletonList rows={4} />}
        {items && !items.length && <EmptyState testid="artifacts-empty" pose="thinking" title="No artifacts yet" text="Ask Bryn for a document, study plan, flashcards or a practice set and it will appear here." action="Start a chat" to="/app/new" />}
        {items?.length > 0 && !shown.length && <p className="text-sm text-neutral-500 text-center py-8" data-testid="artifacts-no-match">Nothing matches your search.</p>}
        <ul className="-mx-3" data-testid="artifacts-list">
          {shown.map((a) => <Row key={a.id} a={a} onOpen={open} onRename={(x) => setDialog({ mode: "rename", a: x })} onDelete={(x) => setDialog({ mode: "delete", a: x })} />)}
        </ul>
      </div>
      {dialog?.mode === "preview" && <Preview a={dialog.a} onClose={close} />}
      {dialog?.mode === "rename" && <RenameDialog a={dialog.a} onClose={close} onSaved={(title) => patch(dialog.a.id, { title })} />}
      {dialog?.mode === "delete" && <DeleteDialog a={dialog.a} onClose={close} onDeleted={() => { setItems((x) => x.filter((y) => y.id !== dialog.a.id)); close(); }} />}
    </div>
  );
}
